"use client";

import { cn } from "@/lib/utils";
import * as Checkbox from "@radix-ui/react-checkbox";
import { PlusIcon, Tag, User2, UserRoundPlus } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { ProjectLabel, ProjectUser } from "./types";
import { UserDropdown } from "./UserDropdown";
import { LabelDropdown } from "./LabelDropdown";
import { useKanbanActions } from "@/hooks/useKanbanActions";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { Canvas } from "@/utils/types";
import { useActiveProject } from "@/utils/utils";

const CanvasLabelDropdown = React.memo(
  ({
    canvas,
    type,
    onChange,
  }: {
    canvas: Canvas | null;
    type?: "new" | "edit" | "PRD";
    onChange?: (newLabels: string[]) => void;
  }) => {
    const [open, setOpen] = useState(false);
    const updateCanvasLabels = useKanbanActions().updateCanvasLabels;
    const updateProjectLabels = useKanbanActions().updateProjectLabels;
    const project = useActiveProject();

    const selectedLabels = useMemo(() => {
      if (!project?.labels || !canvas?.labels) return [];
      return (
        project.labels.filter((label: any) =>
          (canvas?.labels as string[])?.includes(label.id)
        ) || []
      );
    }, [canvas?.labels, project?.labels]);

    const handleChange = useCallback(
      (newLabels: Array<string>) => {
        if (canvas) {
          updateCanvasLabels(canvas, newLabels);
          onChange?.(newLabels);
        }
      },
      [canvas, updateCanvasLabels, onChange]
    );

    const onUpdateProjectLabels = useCallback(
      async (newLabelData: Omit<ProjectLabel, "id">) => {
        if (project?.id && canvas) {
          const newLabel = await updateProjectLabels(
            project,
            project.labels,
            newLabelData
          );
          if (newLabel?.id) {
            const currentCanvasLabels = (canvas.labels as string[]) || [];
            const updatedLabels = [...currentCanvasLabels, newLabel.id];
            handleChange(updatedLabels);
          }
        }
      },
      [project, updateProjectLabels, canvas, handleChange]
    );

    if (!project || !canvas) {
      return null;
    }

    const triggerButton = useMemo(
      () => (
        <Button
          className={cn(
            "!h-6 !text-xs rounded-md px-2 text-secondary-n-400 cursor-default bg-transparent !hover:bg-border-secondary-n-900 !hover:border-0",
            selectedLabels?.length !== 0
              ? "!border-0 !bg-secondary-n-900"
              : "!border-1 !border-secondary-n-900",
            type === "PRD" ? "text-text-primary" : "",
            type === "new"
              ? "bg-background-tertiary cursor-pointer"
              : "bg-transparent cursor-default !hover:bg-border-secondary-n-900"
          )}
          variant="outline"
        >
          {selectedLabels?.length === 0 ? (
            <>
              {type === "PRD" ? (
                <>
                  <Tag className="w-4 h-4" />
                </>
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
              {type === "PRD" ? (
                <span className="pl-1">Label</span>
              ) : (
                <span> Add Label</span>
              )}
            </>
          ) : selectedLabels?.length === 1 ? (
            <div
              key={selectedLabels[0].id}
              className="flex gap-2 items-center text-secondary-n-400"
            >
              {selectedLabels[0].color && (
                <Checkbox.Root
                  className="w-2.5 h-2.5 border rounded-xs flex items-center justify-center"
                  style={{ borderColor: selectedLabels[0].color.value }}
                  defaultChecked
                >
                  <Checkbox.Indicator
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: selectedLabels[0].color.value }}
                  ></Checkbox.Indicator>
                </Checkbox.Root>
              )}
              {selectedLabels[0].label}
            </div>
          ) : (
            <div className="flex gap-2 items-center text-secondary-n-400">
              {selectedLabels.slice(0, 3).map(
                (label: any, index: number) =>
                  label.color && (
                    <Checkbox.Root
                      key={label.id}
                      className={`w-2.5 h-2.5 border rounded-xs flex items-center justify-center ${
                        index > 0 ? "-ml-1.5" : ""
                      }`}
                      style={{ borderColor: label.color.value }}
                      defaultChecked
                    >
                      <Checkbox.Indicator
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: label.color.value }}
                      ></Checkbox.Indicator>
                    </Checkbox.Root>
                  )
              )}
              <span>{selectedLabels.length} labels</span>
            </div>
          )}
        </Button>
      ),
      [selectedLabels]
    );

    return (
      <LabelDropdown
        labels={project?.labels}
        baseSelection={canvas?.labels as string[]}
        triggerElement={triggerButton}
        onUpdateProjectLabels={onUpdateProjectLabels}
        open={open}
        setOpen={setOpen}
        onChange={handleChange}
      />
    );
  }
);

interface CanvasAssignDropdownProps {
  projectId: string;
  canvas: Canvas | null;
  type: "new" | "edit" | "PRD";
  onChange?: (newUserId: string | null) => void;
}

const CanvasAssignDropdown = React.memo(
  ({ projectId, canvas, type, onChange }: CanvasAssignDropdownProps) => {
    const [open, setOpen] = useState(false);
    const usersForProject = useCanvasStore((state) => state.usersForProject);
    const users = useMemo(
      () => usersForProject?.[projectId] || [],
      [usersForProject, projectId]
    );
    const { updateCanvasAssignedUserId } = useKanbanActions();

    const propValue = canvas?.assigned_user_id;

    const assignedUserId = propValue ?? null;

    const selectedUser = useMemo(
      () => users?.find((user) => user.id === assignedUserId) || null,
      [users, assignedUserId]
    );

    const handleAssignUser = useCallback(
      async (selectedUserIds: string[]) => {
        const newUserId = selectedUserIds?.[0] ?? null;
        if (type !== "new" && canvas) {
          await updateCanvasAssignedUserId(canvas, newUserId);
        }
        setOpen(false);
        onChange?.(newUserId);
      },
      [canvas, updateCanvasAssignedUserId, onChange, type]
    );

    const renderUserIcon = useCallback(
      (user: ProjectUser) =>
        user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="select-none focus:outline-none rounded-md h-6 w-6 p-[1px] cursor-pointer"
            crossOrigin="anonymous"
          />
        ) : (
          <User2 className="w-4 h-4" />
        ),
      []
    );

    const buttonTrigger = useMemo(
      () => (
        <Button
          className={cn(
            "!h-6 !text-xs !rounded-md !px-2 !text-secondary-n-400 !hover:border-0",
            selectedUser ? "!p-0 !border-0" : "",
            type === "new"
              ? "bg-background-tertiary cursor-pointer"
              : "bg-transparent cursor-default !hover:bg-border-secondary-n-900"
          )}
          variant="outline"
        >
          {type === "new" ? (
            <div className="flex items-center gap-1 px-1">
              {selectedUser ? (
                renderUserIcon(selectedUser)
              ) : (
                <UserRoundPlus className={`w-4 h-4 `} />
              )}
              <span>{selectedUser?.name || "Assign"}</span>
            </div>
          ) : selectedUser ? (
            renderUserIcon(selectedUser)
          ) : (
            <div
              className={`flex items-center gap-1 ${
                type === "PRD" ? "text-text-primary" : ""
              }`}
            >
              <UserRoundPlus className={`w-4 h-4 `} />
              {type === "PRD" && <span>Assign</span>}
            </div>
          )}
        </Button>
      ),
      [selectedUser]
    );

    return (
      <UserDropdown
        open={open}
        setOpen={setOpen}
        triggerElement={buttonTrigger}
        users={users ?? []}
        onChange={handleAssignUser}
        title="Assign to..."
        baseSelection={assignedUserId ? [assignedUserId] : []}
      />
    );
  }
);

export { CanvasAssignDropdown, CanvasLabelDropdown };
